/**
 * F5 · The ONE code entry control in the funnel.
 *
 * Single-digit boxes, one character each, auto-advancing as they are filled and
 * stepping back on delete. It replaces the old hybrid (display-only boxes above
 * one long input), which showed a code in two places and let the two disagree.
 *
 * Length comes from `OTP_LENGTH` — never a literal digit count here.
 */
import { useEffect, useRef } from "react";
import { OTP_LENGTH } from "@/lib/otp";
import { cn } from "@/lib/utils";

export function OtpInput({
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
  testId = "otp-input",
}: {
  value: string;
  onChange: (next: string) => void;
  /** Fired once the last box is filled, so the caller can submit. */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  testId?: string;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] ?? "");

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function write(next: string) {
    const clean = next.replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(clean);
    /**
     * The completed code is HANDED to the caller and the call is deferred to the
     * next tick. An autofill writes every box in one event, so firing inline ran
     * the caller's submit before React had stored the value and it sent an empty
     * token. The value is passed, so the caller never re-reads state.
     */
    if (clean.length === OTP_LENGTH) {
      const completed = clean;
      setTimeout(() => onComplete?.(completed), 0);
    }
    return clean;
  }

  function distribute(index: number, raw: string) {
    const typed = raw.replace(/\D/g, "");
    if (!typed) return;
    // A paste (or an iOS autofilled one-time code) arrives as one string and
    // fills from this box onwards. Boxes accept a full-length value so the
    // browser never truncates the code before we see it.
    const chars = typed.split("");
    const nextDigits = [...digits];
    let cursor = index;
    for (const ch of chars) {
      if (cursor >= OTP_LENGTH) break;
      nextDigits[cursor] = ch;
      cursor += 1;
    }
    const written = write(nextDigits.join(""));
    const focusAt = Math.min(cursor, OTP_LENGTH - 1);
    if (written.length < OTP_LENGTH) refs.current[focusAt]?.focus();
    else refs.current[OTP_LENGTH - 1]?.blur();
  }

  function handleChange(index: number, raw: string) {
    // When a single box already holds a digit and one more is typed, the value
    // is two characters: treat the newest as the next box's digit.
    if (raw.length === 2 && digits[index] && raw[0] === digits[index]) {
      distribute(index + 1 > OTP_LENGTH - 1 ? index : index + 1, raw[1]);
      return;
    }
    distribute(index, raw);
  }


  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      const nextDigits = [...digits];
      if (nextDigits[index]) {
        nextDigits[index] = "";
        onChange(nextDigits.join("").trimEnd());
      } else if (index > 0) {
        nextDigits[index - 1] = "";
        onChange(nextDigits.join("").trimEnd());
        refs.current[index - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) refs.current[index + 1]?.focus();
  }

  return (
    <div className="mb-4 flex gap-1.5 sm:gap-2" data-testid={testId}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={OTP_LENGTH}
          disabled={disabled}
          value={d}
          aria-label={`Digit ${i + 1}`}
          name={i === 0 ? "one-time-code" : `otp-${i + 1}`}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          onChange={(e) => handleChange(i, e.target.value)}
          onPaste={(e) => {
            e.preventDefault();
            distribute(0, e.clipboardData.getData("text"));
          }}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.currentTarget.select()}

          data-testid={`${testId}-${i + 1}`}
          className={cn(
            "h-[58px] min-w-0 flex-1 rounded-[14px] border bg-white text-center font-brand text-[24px] font-semibold text-ink outline-none",
            "focus:border-grape focus:ring-[3px] focus:ring-grape/25 disabled:opacity-50",
            d ? "border-grape/60" : "border-foreground/[0.18]",
          )}
        />
      ))}
    </div>
  );
}
